import { AlertTriangle, History, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ControllerMode } from "@/data/types";
import { MODE_CATALOG, defaultParamsFor } from "@/data/modeCatalog";
import { useUiPref } from "@/store/uiPrefs";
import { useT } from "@/lib/i18n";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { trapTabKey } from "@/components/CommandPalette";
import { ModePicker } from "./ModePicker";
import { ParamsEditor } from "./ParamsEditor";

/**
 * Manual-override authoring panel. Same wire payload as before
 * (`bess_manual_override` SHARED_SCOPE attribute) — the edge's
 * OverrideIntegration validates and persists. A successful apply lasts
 * for the operator-chosen duration and auto-clears on the edge.
 *
 * The hold-to-confirm guard is mandatory: a one-click override during a
 * peak window can cost real money, so this matches the ems-admin and
 * SchedulePage convention.
 */
const DURATION_PRESETS_MIN = [5, 15, 30, 60, 120, 240];
const DURATION_MIN = 5;
const DURATION_MAX = 1440;

/** Clamp to the accepted window; NaN (cleared custom input) → 30 min. */
function clampDurationMin(v: number): number {
  if (!Number.isFinite(v)) return 30;
  return Math.min(DURATION_MAX, Math.max(DURATION_MIN, Math.round(v)));
}

/** Drop anything that isn't a finite number — `override.last` comes back
 *  from localStorage, so its params must be re-validated before use. */
function sanitizeParams(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

/** Last APPLIED override payload, persisted per site (MKT-15, item 3.9b)
 *  so a repeated intervention ("ConstantPower −100 kW · 60 min — grid
 *  works") is one click + hold instead of four fields re-typed. */
type LastOverride = {
  mode: ControllerMode;
  params: Record<string, number>;
  durationMin: number;
  reason?: string;
};

export function OverridePanel({
  open,
  initialMode,
  initialReason = "",
  fromAlarm = false,
  onApply,
  onClose,
}: {
  open: boolean;
  initialMode: ControllerMode;
  /** R06 — prefill the reason field (e.g. carried from an alarm lever). */
  initialReason?: string;
  /** R06 — render a small "prefilled from alarm" origin note. */
  fromAlarm?: boolean;
  onApply: (payload: { mode: ControllerMode; expires_at: number; params: Record<string, number>; reason?: string }) => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const [mode, setMode] = useState<ControllerMode>(initialMode);
  const [params, setParams] = useState<Record<string, number>>(() => defaultParamsFor(initialMode));
  // NaN is allowed transiently while the custom input is being cleared /
  // retyped; every consumer goes through clampDurationMin().
  const [durationMin, setDurationMin] = useState(30);
  const [reason, setReason] = useState(initialReason);
  const [err, setErr] = useState<string | null>(null);

  const [lastOverride, setLastOverride] = useUiPref<LastOverride | null>("override.last", null);
  // Re-validate the stored pref before offering it: the mode must still
  // exist in the catalog (prefs outlive deploys that rename modes).
  const repeatable =
    lastOverride && typeof lastOverride === "object" && MODE_CATALOG[lastOverride.mode]
      ? lastOverride
      : null;

  const prefillLast = () => {
    if (!repeatable) return;
    setMode(repeatable.mode);
    setParams({ ...defaultParamsFor(repeatable.mode), ...sanitizeParams(repeatable.params) });
    setDurationMin(clampDurationMin(repeatable.durationMin));
    setReason(typeof repeatable.reason === "string" ? repeatable.reason : "");
    setErr(null);
  };

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setParams(defaultParamsFor(initialMode));
      setDurationMin(30);
      // R06 — seed the reason from the carried alarm context (empty for a
      // hand-opened panel), so an alarm-origin override lands pre-explained.
      setReason(initialReason);
      setErr(null);
    }
  }, [open, initialMode, initialReason]);

  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, onClose]);

  // Dialog semantics (XTRA-04): move focus INTO the panel on open (so the
  // trap below has something to hold) and wrap Tab inside the card.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (open) cardRef.current?.focus(); }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-3"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("manage.active.manualOverride")}
        tabIndex={-1}
        onKeyDown={(e) => trapTabKey(e, cardRef.current)}
        className="card w-full max-w-md bg-bg outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-edge px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warn" />
            <div>
              <div className="text-sm font-semibold text-text">{t("manage.active.manualOverride")}</div>
              <div className="text-[11px] text-text-muted">
                {t("manage.override.subtitle")}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md border border-edge p-1.5 text-text-muted hover:bg-surface-2">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4 text-sm">
          {/* R06 — origin note: this panel was opened from an alarm's "Adjust
              dispatch" lever, with the reason prefilled below. Mirrors the
              BessDetail back-to-alarm cue. */}
          {fromAlarm && (
            <div className="flex items-center gap-1.5 rounded-md border border-warn/30 bg-warn/[0.06] px-2.5 py-1.5 text-[11px] text-text-muted">
              <AlertTriangle className="h-3 w-3 shrink-0 text-warn" />
              {t("manage.override.fromAlarm")}
            </div>
          )}
          {repeatable && (
            <button
              type="button"
              onClick={prefillLast}
              title={t("manage.override.repeatTitle")}
              className="inline-flex h-7 items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-2.5 text-[11px] text-accent hover:bg-accent/20"
            >
              <History className="h-3 w-3" />
              {t("manage.override.repeatLast", {
                mode: MODE_CATALOG[repeatable.mode].label,
                minutes: clampDurationMin(repeatable.durationMin),
              })}
            </button>
          )}

          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.override.mode")}</div>
            <ModePicker value={mode} onChange={(m) => { setMode(m); setParams(defaultParamsFor(m)); }} />
            <div className="mt-1 text-[11px] text-text-muted">
              {MODE_CATALOG[mode].summary}
            </div>
          </div>

          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.override.params")}</div>
            <ParamsEditor mode={mode} value={params} onChange={setParams} size="sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.override.duration")}</div>
              <div className="flex gap-2">
                <select
                  value={DURATION_PRESETS_MIN.includes(durationMin) ? String(durationMin) : ""}
                  onChange={(e) => { if (e.target.value !== "") setDurationMin(Number(e.target.value)); }}
                  className="text-input h-9 w-full rounded-md border border-edge bg-surface px-2 text-sm"
                >
                  {!DURATION_PRESETS_MIN.includes(durationMin) && (
                    <option value="" disabled>{t("manage.override.custom")}</option>
                  )}
                  {DURATION_PRESETS_MIN.map((d) => (
                    <option key={d} value={d}>{d} min</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={DURATION_MIN}
                  max={DURATION_MAX}
                  step={5}
                  value={Number.isFinite(durationMin) ? durationMin : ""}
                  onChange={(e) => setDurationMin(e.target.value === "" ? Number.NaN : Number(e.target.value))}
                  onBlur={() => setDurationMin((v) => clampDurationMin(v))}
                  aria-label={t("manage.override.customDurationAria")}
                  title={t("manage.override.customDurationTitle")}
                  className="text-input h-9 w-full rounded-md border border-edge bg-surface px-2 text-sm"
                />
              </div>
            </label>
            <label className="block">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.override.reason")}</div>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={200}
                className="text-input h-9 w-full rounded-md border border-edge bg-surface px-2 text-sm"
                placeholder={t("manage.override.optional")}
              />
            </label>
          </div>

          {err && <div className="text-[11px] text-critical">{err}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-edge px-4 py-3">
          <button
            onClick={onClose}
            className="h-9 rounded-md border border-edge px-3 text-xs text-text-muted hover:bg-surface-2"
          >
            {t("common.cancel")}
          </button>
          <HoldToConfirm
            label={t("manage.override.holdToApply")}
            intent={mode === "OFF" ? "destructive" : "warn"}
            onConfirm={async () => {
              setErr(null);
              const dur = clampDurationMin(durationMin);
              try {
                await onApply({
                  mode,
                  params,
                  expires_at: Date.now() + dur * 60_000,
                  reason: reason.trim() || undefined,
                });
                // Remember the APPLIED payload (not mere edits) so the
                // "Repeat last" chip offers exactly what last ran.
                setLastOverride({ mode, params, durationMin: dur, reason: reason.trim() || undefined });
                onClose();
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
